import { Link, Outlet } from 'react-router-dom';
import { Building2, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@shared/components/common';

/**
 * Main layout with header and footer
 * Used for public pages (Home, PropertyDetails, etc.)
 */
export function MainLayout() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">{t('common.appName')}</span>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className="text-slate-600 hover:text-emerald-600 transition-colors font-medium"
              >
                {t('nav.home')}
              </Link>
              <a
                href="#"
                className="text-slate-600 hover:text-emerald-600 transition-colors font-medium"
              >
                {t('nav.about')}
              </a>
              <a
                href="#"
                className="text-slate-600 hover:text-emerald-600 transition-colors font-medium"
              >
                {t('nav.contact')}
              </a>
            </nav>

            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <a
                href="tel:920000000"
                className="flex items-center gap-2 text-emerald-600 font-medium"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden md:inline" dir="ltr">
                  920 000 000
                </span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">{t('common.appName')}</span>
              </div>
              <p className="text-slate-400 text-sm">
                {t('footer.tagline')}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('footer.quickLinks')}</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>
                  <Link to="/" className="hover:text-white transition-colors">
                    {t('nav.home')}
                  </Link>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    {t('nav.properties')}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    {t('nav.about')}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('footer.support')}</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    {t('footer.faq')}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    {t('footer.contactUs')}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    {t('footer.terms')}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('footer.contactUs')}</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>{t('footer.location')}</li>
                <li dir="ltr">920 000 000</li>
                <li>info@projectalpha.sa</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-500">
            {t('footer.copyright')}
          </div>
        </div>
      </footer>
    </div>
  );
}
